define((require) => {
    "use strict";

    const _ = require("underscore");
    const $ = require("jquery");

    /**
     * Utility class for Promises/Deferreds. Also defines the Promise class to stop IntelliJ warning about it not being found.
     *
     * The actual Promise/Deferred class is from jQuery, see <a href="http://api.jquery.com/category/deferred-object/">jQuery Deferred documentation</a>.
     *
     * @author Bo Gotthardt
     * @class Promise
     * @constructor
     */
    function Promise() {}

    /**
     * See <a href="http://api.jquery.com/deferred.then/">deferred.then()</a>.
     *
     * @param {Function} doneFilter
     * @param {Function} [failFilter]
     * @param {Function} [progressFilter]
     */
    // eslint-disable-next-line no-unused-vars
    Promise.prototype.then = (doneFilter, failFilter, progressFilter) => {
    };
    /**
     * See <a href="http://api.jquery.com/deferred.done/">deferred.done()</a>.
     *
     * @param {Function} callback
     */
    // eslint-disable-next-line no-unused-vars
    Promise.prototype.done = (callback) => {
    };
    /**
     * See <a href="http://api.jquery.com/deferred.fail/">deferred.fail()</a>.
     *
     * @param {Function} callback
     */
    // eslint-disable-next-line no-unused-vars
    Promise.prototype.fail = (callback) => {
    };
    /**
     * See <a href="http://api.jquery.com/deferred.progress/">deferred.progress()</a>.
     *
     * @param {Function} callback
     */
    // eslint-disable-next-line no-unused-vars
    Promise.prototype.progress = (callback) => {
    };
    /**
     * See <a href="http://api.jquery.com/deferred.always/">deferred.always()</a>.
     *
     * @param {Function} callback
     */
    // eslint-disable-next-line no-unused-vars
    Promise.prototype.always = (callback) => {
    };

    // TODO Fix these to work with any number of arguments.
    // There seems to be a problem with new $.Deferred().reject.apply() returning window.
    /**
     * Create a rejected promise.
     *
     * @static
     *
     * @param {*} [arg]
     * @return {Promise}
     */
    Promise.rejected = (arg) => new $.Deferred().reject(arg).promise();

    /**
     * Create a resolved promise.
     *
     * @static
     *
     * @param {*} [arg]
     * @return {Promise}
     */
    Promise.resolved = (arg) => new $.Deferred().resolve(arg).promise();

    /**
     * Creates a rejected or resolved promise depending on the "truthness" of <tt>rejected</tt>.
     *
     * @param {*}       rejected Truthy to create a rejected promise, falsey to create a resolved promise.
     * @param {*}       [arg]    The optional argument to reject or resolve.
     * @return {Promise} The rejected/resolved promise; never falsey.
     */
    Promise.completed = (rejected, arg) => {
        if (rejected) {
            return Promise.rejected(arg);
        }

        return Promise.resolved(arg);
    };

    /**
     * Creates a new deferred that can be signalled as resolved or rejected, optionally with the supplied
     * extended properties. If a deferred worker is supplied as the first argument, a promise will be returned
     * instead, as the worker will be called with the created deferred and can thus resolve or reject the
     * backing deferred.
     *
     * @param  {Function|*} [worker] Optional deferred worker to be called by the deferred as itself as the sole argument.
     *                               The worker can then resolve or reject the deferred as required. If not a function,
     *                               assumed to be <tt>props</tt>.
     * @param  {*}          [props]  The additional (key,value) property pairs to augment the returned deferred with,
     *                               if any. Can also be supplied as the first argument if no worker is used.
     *                               Use carefully - will override existing deferred properties like "resolve",
     *                               "reject", etc.!
     * @return {$.Deferred|Promise} A newly created (augmented) deferred, or a promise for said deferred if
     *                              <tt>worker</tt> is supplied; never null.
     *
     * @see     #create
     */
    Promise.deferred = (worker, props) => {
        if (!_.isFunction(worker)) {
            props = worker;
            worker = undefined;
        }
        const deferred = new $.Deferred(worker);
        if (props) {
            _.extend(deferred, props);
        }
        return worker ? deferred.promise() : deferred;
    };

    /**
     * Creates a new promise from the supplied deferred, optionally with the supplied extended properties.
     *
     * @param  {$.Deferred} deferred The deferred to get the promise for; cannot be falsey.
     * @param  {*}          [props]  The additional (key,value) property pairs to augment the returned promise with,
     *                               if any. Use carefully - will override existing deferred properties like "done",
     *                               "fail", etc.!
     * @return {Promise} A newly created (augmented) promise for <tt>deferred</tt>; never null.
     *
     * @see     #deferred
     */
    Promise.create = (deferred, props) => {
        const promise = deferred.promise();
        if (props) {
            _.extend(promise, props);
        }
        return promise;
    };

    /**
     * Set up the specified list of subordinate deferreds to progress the combined deferred when done.
     *
     * @param {*[]} subordinates
     * @param {Deferred} combinedDeferred
     */
    function notification(subordinates, combinedDeferred) {
        let numDone = 0;

        _.each(subordinates, (subordinate) => {
            // The subordinates can be both promises and already computed synchronous values.
            // This is the same check as in $.when().
            if (Promise.isPromise(subordinate)) {
                subordinate.done((arg) => {
                    numDone++;
                    combinedDeferred.notify.call(combinedDeferred, arg, numDone / subordinates.length);
                });
            } else {
                numDone++;
                combinedDeferred.notify.call(combinedDeferred, subordinate, numDone / subordinates.length);
            }
        });
    }

    /**
     * An improved version of $.when() that returns the values as a single list. <p>
     *
     * Also progresses when individual subordinates are done. The progress event will have the resolved Promise's
     * value and the percentage of subordinates that are done as parameters. <p>
     *
     * Note that due to the way progress() behaves, this function has a rather subtle gotcha when one or more of
     * the subordinates are done already (i.e. non-Promises or already resolved Promises).
     * These will then trigger the progress events <b>synchronously</b> while inside this function call. And unlike
     * done() and fail(), progress() handlers attached later are not called with previously triggered events.
     * So it is therefore not guaranteed how many progress events the caller will actually get, unless they create
     * their own deferred, set up a progress handler and only then pass it as the combinedDeferred parameter.
     *
     * @static
     *
     * @param {*[]} subordinates The list of subordinates, either Promises or arbitrary values.
     * @param {Deferred} [combinedDeferred] The "combined" deferred (not Promise) to use, instead of creating it internally.
     * @return {Promise} A promise for the list of the values of all the subordinates.
     *                   The promise interface of combinedDeferred if that was passed.
     */
    Promise.all = (subordinates, combinedDeferred) => {
        // We would like the returned promise to progress whenever an individual promise has resolved, but $.when()
        // does not support that. So we have to create our own deferred that can be resolved by $.when(), and
        // progressed by done() from the individual promises.
        combinedDeferred = combinedDeferred || new $.Deferred();

        notification(subordinates, combinedDeferred);

        $.when.apply(this, subordinates)
            .done((...args) => {
                // Return the subordinates' values as one list, instead of as individual arguments.
                combinedDeferred.resolve(Array.prototype.slice.call(args, 0));
            })
            .fail(combinedDeferred.reject);

        return combinedDeferred.promise();
    };

    /**
     * Returns a new promise from the one specified, that will check/call the provided predicate
     * to perform a truth test. If the test fails (predicate returns <tt>false</tt>), the promise
     * will be rejected.
     *
     * @param  {Promise} promise The promise to intercept and filter based on <tt>predicate</tt>; cannot be falsey.
     * @param  {Function|{predicate:Function, resolve: Function([])->Object, reject:*}} predicate The predicate callback
     *              performing the truth test, or as options storing said predicate, optional <tt>resolve</tt> function
     *              to return resolved value based on arguments supplied to the "then" handler, and optional
     *              <tt>reject</tt> property storing value to reject when <tt>predicate()</tt> returns a non-truthy
     *              value. If <tt>resolve</tt> is not specified, the first argument passed to the "then" handler
     *              will be resolved (returned).
     * @return {Promise} The new promise to use in place of <tt>promise</tt>; never falsey.
     */
    Promise.predicate = (promise, predicate) => {
        const options = _.isFunction(predicate) ? {predicate, resolve: null, reject: null} : predicate;

        // Make sure we have a resolve function...
        if (!_.isFunction(options.resolve)) {
            options.resolve = function(args) { return args[0]; };
        }

        return promise.then((...args) => {
            try {
                // All good, pass on argument (if any) and return
                if (options.predicate()) {
                    return options.resolve(args);
                }
                return Promise.rejected(options.reject);
            } catch (error) {
                // If an error occurs during execution of the callback
                return Promise.rejected(options.reject || error);
            }
        });
    };

    /**
     * Alternative version of $.when() that always resolves with a list of the return vales of the subordinates that resolved.
     *
     * Also progresses when individual subordinates are done. The progress event will have the resolved Promise's
     * value and the percentage of subordinates that are done as parameters.
     *
     * See Promise.all() for a note on how the progress events work.
     *
     * @static
     *
     * @param {*[]} subordinates The list of subordinates, either Promises or arbitrary values.
     * @param {Deferred} [combinedDeferred] The "combined" deferred (not Promise) to use, instead of creating it internally.
     * @return {Promise} A promise for a list of the values of the subordinates that resolved.
     */
    Promise.any = function(subordinates, combinedDeferred) {
        combinedDeferred = combinedDeferred || new $.Deferred();

        notification(subordinates, combinedDeferred);

        subordinates = _.map(subordinates, (subordinate) => {
            if (Promise.isPromise(subordinate)) {
                // Always resolve subordinates rather than reject so the when() deferred always resolves.
                return subordinate.then(null, () => Promise.resolved());
            }
            return subordinate;
        });

        $.when.apply(this, subordinates)
            .done((...args) => {
                // Return the subordinates as one list, instead of as individual arguments.
                // Filter out empty values caused by the resolution above.
                combinedDeferred.resolve(Array.prototype.filter.call(args, (item) => item !== undefined));
            });

        return combinedDeferred.promise();
    };

    /**
     * @static
     * Determine whether the specified argument is a Promise.
     *
     * @param {*} possiblePromise The object to test if a promise; can be falsey. Note that a $.Deferred type will also
     *                            cause true to be returned.
     * @return {Boolean} True if a promise, false otherwise.
     */
    Promise.isPromise = (possiblePromise) => possiblePromise && _.isFunction(possiblePromise.promise); // This is how jQuery#when() does it internally.

    /**
     * @static
     * Determine whether the specified argument is a $.Deferred type (thus also doubling as a promise for itself).
     *
     * @param {*} possibleDeferred The object to test if a deferred; can be falsey. Note that a Promise type will not
     *                              cause true to be returned, but false (unless also a $.Deferred).
     * @return {Boolean} True if a deferred (and promise), false otherwise.
     */
    Promise.isDeferred = (possibleDeferred) => possibleDeferred && _.isFunction(possibleDeferred.resolve);

    /**
     * Whether the specified promise has a pending state, i.e. <tt>promise.state() === "pending"</tt>.
     *
     * @param  {Promise}  promise The promise to check.
     * @return {Boolean}          <tt>true</tt> if the specified promise has a pending state, <tt>false</tt> if not.
     */
    Promise.isPending = (promise) => promise.state() === Promise.State.PENDING;

    /**
     * Takes a deferred and creates a new promise which will time out after the specified ms, to be rejected with
     * the specified value, if any, after said timeout if not already resolved or rejected. <p>
     *
     * The returned promise will be augmented with a <tt>clearTimeout()</tt> function that will clear the timeout,
     * if so needed, and an <tt>age()</tt> function that will return the promise age in ms (can be larger than
     * the timeout). <p>
     *
     * Note: the return value from this function (<tt>Promise</tt>) differs from that of <tt>timeout(..)</tt>!
     *
     * @param  {Deferred} deferred  The deferred create a timed promise for; cannot be falsey.
     * @param  {Number}   [timeout] The timeout in ms; defaults to 20s.
     * @param  {*}        [arg]     Optional value to reject; can be falsey, will default to "timeout".
     *
     * @return {Promise} A new promise that will timeout; never null. Will be augmented
     *                   with a <tt>clearTimeout()</tt> function to explicitly clear the timeout if needed.
     */
    Promise.timeout = (deferred, timeout, arg) => {
        const promise = deferred.promise();
        Promise.time(deferred, promise, timeout, arg);
        return promise;
    };

    /**
     * Takes a deferred and pre-existing promise and ensures the deferred will be rejected with the supplied argument
     * after the specified timeout in ms from "now" if and only if the supplied pre-existing promise is still pending
     * after said timeout. <p>
     *
     * <tt>promise</tt> need not have been created by <tt>deferred</tt>. <p>
     *
     * <tt>promise</tt> will be augmented with a <tt>clearTimeout()</tt> function that will clear the timeout,
     * if so needed, and an <tt>age()</tt> function to returns its age in ms (can be larger than timeout). If
     * <tt>promise</tt> already has an old associated timeout, i.e. this function already called for it, it will
     * be cleared before the new timeout is set ("re-start"). On timeout or when <tt>promise</tt> is resolved,
     * <tt>clearTimeout</tt> will be removed from <tt>promise</tt> again.
     *
     * Note: the return value from this function (<tt>Function</tt>) differs from that of <tt>time(..)</tt>!
     *
     * @param  {Deferred} deferred  The deferred to reject on timeout of <tt>promise</tt>; cannot be falsey.
     * @param  {Promise}  promise   A pre-existing promise, which does not have to be a promise for <tt>deferred</tt>,
     *                              that must be completed with <tt>timeout</tt> or <tt>deferred</tt> will be
     *                              rejected; cannot be falsey.
     * @param  {Number}   [timeout] The timeout in ms; defaults to 20s.
     * @param  {*}        [arg]     Optional value to reject; can be falsey, will default to "timeout".
     *
     * @return {Function} the <tt>clearTimeout()</tt> function also set on <tt>promise</tt>; never falsey.
     */
    Promise.time = (deferred, promise, timeout, arg) => {
        let timer;
        const time = timeout || 20000;

        // Timeout callback...
        const callback = function() {
            clear();
            // Must still be pending to be rejected, e.g. could have been resolved...
            if (Promise.isPending(promise)) {
                deferred.reject(typeof arg === "undefined" ? "timeout" : arg); // to allow 0 (number)
            }
        };

        // Clear timeout callback...
        const clear = () => {
            if (timer) {
                clearTimeout(timer);
                timer = null;
                delete promise.clearTimeout;
            }
            return promise.age();
        };

        const start = Date.now();

        // Clear old timeout on promise re-use...
        if (_.isFunction(promise.clearTimeout)) {
            promise.clearTimeout();
        }

        // Augment with (new) "clearTimeout" function to let the context explicitly clear the timeout if needed,
        // and we give it a nice "toString" function to easily see the current promise age/state...
        promise.clearTimeout = clear;
        promise.age = function() { return Date.now() - start; };
        promise.toString = function() {
            return `Promise[state: ${promise.state()}; age: ${promise.age()}/${time}]`;
        };

        if (time >= 0) {
            timer = setTimeout(callback, time);

        // Negative time means timeout...
        } else {
            callback();
        }

        // Make sure we clear timeout on completion. If "promise" is already complete, this will actually call "clear"
        // and clean up...
        promise.always(clear);

        return clear;
    };

    /**
     * Enumeration describing the promise state(s), as the values returned by <tt>promise.state()</tt>.
     *
     * @type {Object}
     */
    Promise.State = {
        // The Deferred object can be in one of three states.
        PENDING: "pending",
        RESOLVED: "resolved",
        REJECTED: "rejected"
    };

    return Promise;
});



// WEBPACK FOOTER //
// ./src/shared/util/Promise.js